import { useEffect, useMemo, useState } from "react";
import { getToken } from "../utils/auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

export default function LeetCodeLanguageStats({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");

  const maxSolved = useMemo(() => {
    if (!stats?.length) return 1;
    return Math.max(...stats.map((item) => item.problemsSolved || 0), 1);
  }, [stats]);

  useEffect(() => {
    if (!studentId) return;

    async function fetchLanguageStats() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/student/get-leetcode-language-stats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ studentId }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || "Unable to load language statistics.");
        }

        setUsername(data.username || "");
        setStats(Array.isArray(data.languageStats) ? data.languageStats : []);
      } catch (err) {
        console.error("LeetCode language stats error", err);
        setError(err.message || "Could not fetch language statistics.");
      } finally {
        setLoading(false);
      }
    }

    fetchLanguageStats();
  }, [studentId]);

  return (
    <section className="leetcode-language-stats">
      <div className="lc-section-title-wrapper">
        <h3 className="lc-panel-title">Language Usage</h3>
        {username && <span className="language-subtitle">Stats for {username}</span>}
      </div>

      {loading && (
        <div className="language-stats-loading">
          <div className="loader" />
          <p>Fetching language distribution...</p>
        </div>
      )}

      {!loading && error && <div className="language-stats-error">{error}</div>}

      {!loading && !error && stats.length > 0 && (
        <div className="language-list-grid">
          {stats.map((language) => {
            const solved = language.problemsSolved || 0;
            const width = Math.min((solved / maxSolved) * 100, 100);
            return (
              <div key={language.languageName} className="language-row">
                <div className="language-meta">
                  <span className="language-name">{language.languageName}</span>
                  <span className="language-count">{solved} solved</span>
                </div>
                <div className="language-bar-bg">
                  <div className="language-bar-fill" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && stats.length === 0 && (
        <div className="language-stats-empty">No language usage data available yet.</div>
      )}
    </section>
  );
}
